import { assert, assertNonNullable } from '@disy/lib/custom-error';
import { logger } from '@disy/lib/logging';
import { memoize } from '@disy/lib/memoize';

import { isTemplateObjecttype } from 'cadenza/acquisition/acquisition-layer-utils';
import { getVisibleObjecttypes } from 'cadenza/api-client/objecttypes-api';
import type { Dataset, DatasetKey } from 'cadenza/api-client/self-service-api';
import { loadDatasetsOfObjecttype } from 'cadenza/api-client/self-service-api';
import { isFeatureAvailable } from 'cadenza/features';
import { isCdsSrsWithMaxExtentConfigured } from 'cadenza/objecttype/objecttype-details/objecttype-utils';
import type { DataAcquisitionActionType } from 'cadenza/repository/model/data-acquisition-action-type.gen';
import type { ObjecttypeIdString } from 'cadenza/repository/model/objecttype-id.gen';
import type { RootStateOr } from 'cadenza/store/cadenza-store';
import { getStore } from 'cadenza/store/cadenza-store';
import {
  getDataAcquisitionModalWithGeometry,
  isDataAcquisitionModalWithGeometryOpen,
} from 'cadenza/workbook/acquisition/data-acquisition-modal-with-geometry/data-acquistion-modal-with-geometry-util';
import type { DataViewDto } from 'cadenza/workbook/api/data-view-dto';
import { DataViewVisualizationKey, getViewType } from 'cadenza/workbook/api/data-view-visualization-key';
import type { LayerId } from 'cadenza/workbook/api/layer-id';
import type { Objecttype } from 'cadenza/workbook/api/objecttype';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import {
  getEditGeometryDialog,
  isEditGeometryDialogOpen,
} from 'cadenza/workbook/edit-geometry-dialog/edit-geometry-dialog-util';
import { sortDataViewIdsBasedOnLayerTree } from 'cadenza/workbook/map/workbook-layer-utils';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import { cleanupStateAfterDataAcquisition } from 'cadenza/workbook/selection-cart/selection-utils';
import {
  getAttributeById,
  getDataViewById,
  getObjecttypeById,
  getViewByDataViewId,
  getViewById,
  isRepositoryPublishingProcessEnabled,
  isWorkbookVisibleRestricted,
  workbookRepositoryHasCmdb,
} from 'cadenza/workbook/workbook-core-selectors.state';
import type { WorkbookState } from 'cadenza/workbook/workbook.state';

const VALID_VISUALIZATIONS = [
  DataViewVisualizationKey.MAP.CIRCLE_SECTORS,
  DataViewVisualizationKey.MAP.CLUSTERING_POINT,
  DataViewVisualizationKey.MAP.HEATMAP,
  DataViewVisualizationKey.MAP.LINE,
  DataViewVisualizationKey.MAP.POINT,
  DataViewVisualizationKey.MAP.POLYGON,
  DataViewVisualizationKey.MAP,
];

// Currently, it is enough to fetch the objecttypes once per page load
const cachedGetObjecttypesTemplates = memoize(getObjecttypesTemplates);

async function getObjecttypesTemplates() {
  const objecttypes = await getVisibleObjecttypes({
    filters: {
      canBeUsedAsTemplate: 'true',
    },
  });
  return objecttypes.rows.filter(isTemplateObjecttype);
}

export async function hasVisibleObjecttypesThatCanBeUsedAsTemplate() {
  return (await cachedGetObjecttypesTemplates()).length > 0;
}

export async function canCreateEmptyObjecttypeWithGeometry() {
  return (
    workbookRepositoryHasCmdb(getStore().getState()) &&
    isWorkbookVisibleRestricted(getStore().getState()) &&
    !isRepositoryPublishingProcessEnabled(getStore().getState()) &&
    isFeatureAvailable('CREATE_EMPTY_OBJECTTYPE') &&
    (await isCdsSrsWithMaxExtentConfigured())
  );
}

export function isAcquisitionDataView(state: RootStateOr<WorkbookState>, dataViewId: DataViewId) {
  const dataView = getDataViewById(state, dataViewId);

  if (VALID_VISUALIZATIONS.indexOf(dataView.visualizationKey) < 0) {
    return false;
  }

  const objecttypeId = getAcquisitionObjecttypeIdByDataView(state, dataView);

  // No acquisition if no objecttype could be determined
  if (!objecttypeId) {
    return false;
  }
  // No acquisition for views that don't support it
  const requiredDataAcquisitionActionType = getRequiredDataAcquisitionActionType(dataView.visualizationKey);
  if (!requiredDataAcquisitionActionType) {
    return false;
  }
  return canDoDataAcquisitionOnObjecttype(state, objecttypeId, requiredDataAcquisitionActionType);
}

function canDoDataAcquisitionOnObjecttype(
  state: RootStateOr<WorkbookState>,
  objecttypeId: ObjecttypeIdString,
  dataAcquisitionActionType: DataAcquisitionActionType,
) {
  const objecttype = getObjecttypeById(state, objecttypeId);
  return !!objecttype?.allowedDataAcquisitionActionTypes?.includes(dataAcquisitionActionType);
}

export function canTheoreticallyDoDataAcquisitionOnObjecttype(
  state: RootStateOr<WorkbookState>,
  objecttypeId: ObjecttypeIdString,
  dataAcquisitionActionType: DataAcquisitionActionType,
) {
  const objecttype = getObjecttypeById(state, objecttypeId);
  return !!objecttype?.allDataAcquisitionActionTypes?.includes(dataAcquisitionActionType);
}

function getRequiredDataAcquisitionActionType(
  visualizationKey: DataViewVisualizationKey,
): DataAcquisitionActionType | undefined {
  const viewType = getViewType(visualizationKey);
  if (viewType === 'MAP') {
    return 'ACQUIRE_LAYER_DATA';
  }
  if (viewType === 'TABLE') {
    return 'ACQUIRE_TABLE_DATA';
  }
  return undefined;
}

/**
 * Returns true if the user is allowed to add, update and delete rows from the given acquisition
 * layer's objecttype.
 *
 * @param state - Workbook state
 * @param dataViewId - dataView to check possibility of acquisition for
 * @return true if allowed
 */
export function canAcquireDataForDataView(state: RootStateOr<WorkbookState>, dataViewId: DataViewId) {
  if (!isFeatureAvailable('ACQUIRE_OBJECTTYPE_DATA')) {
    return false;
  }

  return isAcquisitionDataView(state, dataViewId);
}

export function getAcquisitionDataViewsIds(state: RootStateOr<WorkbookState>, viewId: ViewId) {
  return getViewById(state, viewId).dataViews.filter((dataViewId) => isAcquisitionDataView(state, dataViewId));
}

export function getAcquisitionObjecttypeIdByDataView(state: RootStateOr<WorkbookState>, dataView: DataViewDto) {
  const geometryBundle = dataView.attributes['GEOMETRY'];
  if (!geometryBundle?.length) {
    return; // Return undefined if the geometry bundle is empty
  }
  const geometryAttributeId = geometryBundle[0];
  const geometryAttribute = getAttributeById(state, geometryAttributeId);
  return geometryAttribute.objecttypeId;
}

export function getAcquisitionObjecttypesInDataView(viewId: ViewId): ObjecttypeIdString[] {
  return getAcquisitionDataViewsIds(getStore().getState(), viewId)
    .map((dataViewId) => {
      const dataView = getDataViewById(getStore().getState(), dataViewId);
      const geometryAttributeId = dataView.attributes['GEOMETRY']![0]; // Simple points have only one
      const geometryAttribute = getAttributeById(getStore().getState(), geometryAttributeId);
      const objecttype = getObjecttypeById(getStore().getState(), geometryAttribute.objecttypeId);
      return objecttype.properties?.['source-acquisition-template'];
    })
    .filter((objecttypeId) => !!objecttypeId) as ObjecttypeIdString[];
}

export async function createOrEditDataset(dataViewId: DataViewId, editData?: DatasetKey) {
  try {
    if (isDataAcquisitionModalWithGeometryOpen()) {
      const dialog = getDataAcquisitionModalWithGeometry();
      assertNonNullable(editData, 'switching to create mode when dialog is already open is not supported.');
      await dialog.setEditMode(dataViewId, editData);
      return;
    } else if (isEditGeometryDialogOpen()) {
      const dialog = getEditGeometryDialog();
      assertNonNullable(editData, 'switching to create mode when dialog is already open is not supported.');
      await dialog.setEditMode(dataViewId, editData);
      return;
    } else {
      const { DataAcquisitionModalWithGeometry } =
        await import('cadenza/workbook/acquisition/data-acquisition-modal-with-geometry/data-acquisition-modal-with-geometry');
      const viewId = getViewByDataViewId(getStore().getState(), dataViewId).id;
      const dialog = new DataAcquisitionModalWithGeometry(viewId);
      if (editData) {
        await dialog.setEditMode(dataViewId, editData);
      } else {
        await dialog.setCreateMode(dataViewId);
      }
      const result = await dialog.connectAndShow();
      if (result) {
        const { extent, editedObjectTypeIds, editedDatasetKeys } = result;
        if (editedObjectTypeIds.length > 0) {
          // We reset the selection. Why? CADENZA-40513
          // If a feature was deleted from inside the daq dialog, we cannot easily determine which one it was at this point,
          // and if this exact feature was selected on the workbook map, it will break. For now, we simply clear all selections.
          const clearSelection = editedDatasetKeys.length === 0;
          cleanupStateAfterDataAcquisition(editedObjectTypeIds, editedDatasetKeys, clearSelection);
        }
        return extent;
      }
    }
  } catch (e) {
    return logger.error('Error in data acquisition modal with geometry', e);
  }
}

export async function createNewDataSet(viewId: ViewId, preselectedDataViewIds: DataViewId[] = []) {
  let acquisitionDataViewIds = getAcquisitionDataViewsIds(getStore().getState(), viewId);
  assert(acquisitionDataViewIds.length, 'No acquisition layers available');

  if (preselectedDataViewIds.length) {
    assert(
      preselectedDataViewIds.every((id) => acquisitionDataViewIds.includes(id)),
      'Selected dataView ids must support data Acquisition',
    );
    acquisitionDataViewIds = preselectedDataViewIds;
  }

  const dataViewId =
    acquisitionDataViewIds.length > 1 ?
      await showSelectAcquisitionLayerDialog(sortDataViewIdsBasedOnLayerTree(viewId, acquisitionDataViewIds))
    : acquisitionDataViewIds[0];

  if (!dataViewId) {
    return; // User has cancelled the select acquisition layer dialog
  }
  return createOrEditDataset(dataViewId);
}

export function getAcquisitionObjecttypeIdByDataViewId(dataViewId: DataViewId) {
  const dataView = getDataViewById(getStore().getState(), dataViewId);
  return getAcquisitionObjecttypeIdByDataView(getStore().getState(), dataView)!;
}

export async function deleteDatasets(
  objecttype: Objecttype,
  dataAcquisitionActionType: DataAcquisitionActionType,
  datasetKeys: DatasetKey[],
) {
  const { openDeleteDatasetsDialog } =
    await import('cadenza/workbook/acquisition/delete-datasets-dialog/delete-datasets-dialog');
  const datasetsWithMetaData = await loadDatasetsOfObjecttype(objecttype.id, datasetKeys, undefined, false);
  return openDeleteDatasetsDialog({ objecttype, datasetKeys, datasetsWithMetaData, dataAcquisitionActionType });
}

function showSelectAcquisitionLayerDialog(acquisitionDataViewIds: DataViewId[]) {
  return import('cadenza/workbook/acquisition/select-acquisition-layer-dialog/select-acquisition-layer-dialog').then(
    ({ SelectAcquisitionLayerDialog }) => new SelectAcquisitionLayerDialog(acquisitionDataViewIds).connectAndShow(),
  );
}

export async function showAddAcquisitionLayerWizard(viewId: ViewId, parentLayerId?: LayerId) {
  try {
    const { AddDataAcquisitionLayerWizard } =
      await import('cadenza/workbook/acquisition/add-data-acquisition-layer-wizard/add-data-acquisition-layer-wizard');

    const objecttypeTemplates = await cachedGetObjecttypesTemplates();
    return await new AddDataAcquisitionLayerWizard(viewId, objecttypeTemplates, parentLayerId).connectAndShow();
  } catch (error) {
    logger.error('Failed to show create data source dialog', error);
  }
}

export function getDatasetTitle(objecttype: Objecttype, dataset: Dataset, printNameAttributeName?: string) {
  const idText =
    printNameAttributeName ?
      dataset[printNameAttributeName]
    : extractKeyAttributeValues(objecttype, dataset).toString();
  return `"${objecttype.printName} - ${idText}"`;
}

function extractKeyAttributeValues(objecttype: Objecttype, dataset: Dataset) {
  return (
    objecttype.attributes
      .map((attributeId) => getAttributeById(getStore().getState(), attributeId))
      .filter((attribute) => attribute.keyAttribute)
      // Key attribute duplicated as measure attribute is still key and not considered during acquisition.
      .filter((attribute) => !attribute.localAttributeType)
      .map((attribute) => dataset[attribute.name]?.toString() ?? attribute.nullValueDisplayText)
  );
}
